import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";

import { adminSettingsApi } from "@profitlens/api-client";
import type { AiPricingOut } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Button } from "@profitlens/ui/button";
import { Card, CardContent } from "@profitlens/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@profitlens/ui/dialog";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import { Switch } from "@profitlens/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";

import { PageHeader } from "@/components/page-header";
import { translateError } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";

export function AiPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ["admin", "settings", "ai"],
    queryFn: () => adminSettingsApi.getAi(),
  });
  const pricing = useQuery({
    queryKey: ["admin", "settings", "ai-pricing"],
    queryFn: () => adminSettingsApi.listAiPricing(),
  });

  const [provider, setProvider] = useState<string>("");
  const [model, setModel] = useState("");
  const [budget, setBudget] = useState("");
  const [rateLimit, setRateLimit] = useState("30");
  const [rateEnforced, setRateEnforced] = useState(true);
  const [feedback, setFeedback] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [editing, setEditing] = useState<AiPricingOut | "new" | null>(null);

  useEffect(() => {
    const s = settings.data;
    if (!s) return;
    setProvider(s.provider_source === "db" ? s.provider : "");
    setModel(s.model_source === "db" ? s.model : "");
    setBudget(s.monthly_budget_usd);
    setRateLimit(String(s.rate_limit_per_minute));
    setRateEnforced(s.rate_limit_enforced);
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      adminSettingsApi.patchAi({
        provider: provider ? (provider as "together" | "openai" | "mock") : null,
        model: model || null,
        monthly_budget_usd: budget || null,
        // 空串 = 不提交该字段；"0" 是合法的"不限流"
        rate_limit_per_minute: rateLimit.trim() === "" ? null : Number(rateLimit),
        rate_limit_enforced: rateEnforced,
      }),
    onSuccess: async () => {
      setFeedback({ kind: "ok", text: t("common.saved") });
      await queryClient.invalidateQueries({ queryKey: ["admin", "settings", "ai"] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "audit-logs"] });
    },
    onError: (err) => setFeedback({ kind: "err", text: translateError(err, t) }),
  });

  if (settings.isLoading) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }
  if (settings.error) {
    return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;
  }
  const s = settings.data;

  return (
    <div className="space-y-6">
      <PageHeader title={t("settings.aiTitle")} subtitle={t("settings.aiSubtitle")} />

      <Card>
        <CardContent className="space-y-4 p-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>{t("settings.aiProvider")}</Label>
              <Select
                value={provider || "__env__"}
                onValueChange={(v) => setProvider(v === "__env__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__env__">{t("settings.sourceEnv")}</SelectItem>
                  <SelectItem value="together">Together AI</SelectItem>
                  <SelectItem value="openai">OpenAI</SelectItem>
                  <SelectItem value="mock">Mock</SelectItem>
                </SelectContent>
              </Select>
              {s ? (
                <p className="text-xs text-muted">
                  {t("settings.aiProviderSource", {
                    source:
                      s.provider_source === "db" ? t("settings.sourceDb") : t("settings.sourceEnv"),
                  })}
                  {" · "}
                  {s.provider}
                </p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.aiModel")}</Label>
              <Input
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder={t("settings.aiModelPlaceholder")}
              />
              <p className="text-xs text-muted">{t("common.leaveEmptyEnv")}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.aiBudget")}</Label>
              <Input
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="tabular-nums"
              />
              <p className="text-xs text-muted">{t("settings.aiBudgetHint")}</p>
            </div>
          </div>

          <div className="grid gap-4 border-t border-border pt-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("settings.aiRateLimit")}</Label>
              <Input
                value={rateLimit}
                onChange={(e) => setRateLimit(e.target.value)}
                className="tabular-nums"
                inputMode="numeric"
              />
              <p className="text-xs text-muted">{t("settings.aiRateLimitHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.aiRateEnforced")}</Label>
              <label className="flex h-10 items-center gap-2 text-sm">
                <Switch checked={rateEnforced} onCheckedChange={setRateEnforced} />
                {rateEnforced ? t("settings.aiRateEnforcedOn") : t("settings.aiRateEnforcedOff")}
              </label>
              <p className="text-xs text-muted">{t("settings.aiRateEnforcedHint")}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 border-t border-border pt-4">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? t("common.saving") : t("settings.aiSave")}
            </Button>
            {feedback ? (
              <p
                className={feedback.kind === "ok" ? "text-xs text-success" : "text-xs text-danger"}
              >
                {feedback.text}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <PageHeader
          title={t("settings.pricingTitle")}
          subtitle={t("settings.pricingSubtitle")}
          actions={
            <Button variant="outline" onClick={() => setEditing("new")}>
              <Plus className="h-4 w-4" />
              {t("settings.pricingAdd")}
            </Button>
          }
        />
        {pricing.isLoading ? (
          <div className="grid place-items-center py-12">
            <Spinner className="h-5 w-5" />
          </div>
        ) : pricing.error ? (
          <ErrorState error={pricing.error} onRetry={() => pricing.refetch()} />
        ) : pricing.data && pricing.data.length === 0 ? (
          <Card className="p-8 text-center text-sm text-muted">{t("settings.pricingEmpty")}</Card>
        ) : (
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("settings.pricingProvider")}</TableHead>
                  <TableHead>{t("settings.pricingModel")}</TableHead>
                  <TableHead>{t("settings.pricingInput")}</TableHead>
                  <TableHead>{t("settings.pricingOutput")}</TableHead>
                  <TableHead>{t("settings.pricingActive")}</TableHead>
                  <TableHead>{t("settings.pricingSource")}</TableHead>
                  <TableHead>{t("common.updatedAt")}</TableHead>
                  <TableHead className="w-20">{t("common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pricing.data?.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.provider}</TableCell>
                    <TableCell className="font-mono text-xs">{row.model}</TableCell>
                    <TableCell className="tabular-nums">
                      {row.input_price_per_mtok} {row.currency}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {row.output_price_per_mtok} {row.currency}
                    </TableCell>
                    <TableCell>
                      {row.is_active ? (
                        <Badge variant="success">{t("common.yes")}</Badge>
                      ) : (
                        <Badge variant="secondary">{t("common.no")}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted">{row.source ?? "—"}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap text-muted">
                      {row.updated_at ? formatDateTime(row.updated_at) : "—"}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => setEditing(row)}>
                        <Pencil className="h-3.5 w-3.5" />
                        {t("common.edit")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <PricingDialog editing={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function PricingDialog({
  editing,
  onClose,
}: {
  editing: AiPricingOut | "new" | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isNew = editing === "new";
  const existing = editing !== null && editing !== "new" ? editing : null;

  const [provider, setProvider] = useState("together");
  const [model, setModel] = useState("");
  const [inputPrice, setInputPrice] = useState("");
  const [outputPrice, setOutputPrice] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [source, setSource] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (existing) {
      setProvider(existing.provider);
      setModel(existing.model);
      setInputPrice(existing.input_price_per_mtok);
      setOutputPrice(existing.output_price_per_mtok);
      setIsActive(existing.is_active);
      setSource(existing.source ?? "");
    } else {
      setProvider("together");
      setModel("");
      setInputPrice("");
      setOutputPrice("");
      setIsActive(true);
      setSource("");
    }
    setError(null);
  }, [editing, existing]);

  const save = useMutation({
    mutationFn: () => {
      if (isNew) {
        return adminSettingsApi.createAiPricing({
          provider,
          model,
          input_price_per_mtok: inputPrice,
          output_price_per_mtok: outputPrice,
          currency: "USD",
          is_active: isActive,
          source: source || null,
        });
      }
      if (!existing) throw new Error("no pricing");
      return adminSettingsApi.patchAiPricing(existing.id, {
        input_price_per_mtok: inputPrice || null,
        output_price_per_mtok: outputPrice || null,
        is_active: isActive,
        source: source || null,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "settings", "ai-pricing"] });
      onClose();
    },
    onError: (err) => setError(translateError(err, t)),
  });

  return (
    <Dialog open={editing !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? t("settings.pricingAdd") : t("settings.pricingEdit")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("settings.pricingProvider")}</Label>
              <Input
                value={provider}
                disabled={!isNew}
                onChange={(e) => setProvider(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.pricingModel")}</Label>
              <Input
                value={model}
                disabled={!isNew}
                onChange={(e) => setModel(e.target.value)}
                placeholder={t("settings.aiModelPlaceholder")}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.pricingInput")}</Label>
              <Input
                value={inputPrice}
                onChange={(e) => setInputPrice(e.target.value)}
                className="tabular-nums"
                placeholder="0.15"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("settings.pricingOutput")}</Label>
              <Input
                value={outputPrice}
                onChange={(e) => setOutputPrice(e.target.value)}
                className="tabular-nums"
                placeholder="0.47"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("settings.pricingSource")}</Label>
            <Input value={source} onChange={(e) => setSource(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={isActive} onCheckedChange={setIsActive} />
            {t("settings.pricingActive")}
          </label>
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={
              save.isPending || (isNew && (!provider || !model || !inputPrice || !outputPrice))
            }
            onClick={() => save.mutate()}
          >
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
